import { useEffect } from 'react';
import styled from '@emotion/styled';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';

import { tokens, Button, useNotify, Modal } from '@hotjar/hotsauce';
import { useOrganizationsInvalidate } from '@hotjar/webapp/features/Settings/SitesAndOrganizations/useOrganizationsInvalidate';
import { Input } from '@hotjar/webapp/components/Input';
import { useOwnedAccount } from '@hotjar/account';
import type { Organization } from '@hotjar/organization';
import { useTranslationWithPrefix } from '@hotjar/translations';
import { useModalEvents } from '../../useModalEvents';
import { Event, UiPosition } from '../../events';
import * as api from './api';

const FORM_ID = 'edit-organization';
const INPUT_LABEL = 'organization-name';

const S = {
  Label: styled.label`
    ${tokens.text('caption')}
  `,
};

interface FormInputs {
  organizationName: string;
}
interface Props {
  handleSuccess(): void;
  handleCancel(): void;
  opened: boolean;
  organization: Pick<Organization, 'id' | 'name' | 'accountId'>;
}

const TRANSLATION_DOMAIN = 'account:edit-organization';

export function EditOrganizationModal({
  handleSuccess,
  handleCancel,
  opened,
  organization,
}: Props): React.ReactElement {
  const { t } = useTranslationWithPrefix(TRANSLATION_DOMAIN);
  const { data: ownedAccountData } = useOwnedAccount();
  const accountId = ownedAccountData && ownedAccountData.id ? ownedAccountData.id : null;
  const invalidate = useOrganizationsInvalidate();
  const { showToast } = useNotify();
  const { trackClicked, trackOpened } = useModalEvents({
    organizationId: organization.id,
    accountId: organization.accountId,
    confirm: Event.CONFIRM_EDIT_ORGANIZATION_MODAL,
    uiPosition: UiPosition.EDIT_ORGANIZATION_MODAL,
    opened: Event.VIEW_EDIT_ORGANIZATION_MODAL,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    defaultValues: {
      organizationName: organization.name,
    },
  });
  const mutation = useMutation(
    (name: string) => {
      if (accountId !== null) {
        return api.editOrganization(accountId, name, organization.id);
      }
      return Promise.reject();
    },
    {
      onSuccess: () => {
        showToast({ text: t('save-success'), kind: 'success' });
        invalidate();
        handleSuccess();
      },
      onError: () => {
        showToast({ text: t('save-failure'), kind: 'danger' });
      },
    },
  );

  useEffect(() => {
    trackOpened(opened);
  }, [opened, trackOpened]);

  return (
    <Modal isOpen={opened} size="m" contentLabel={t('title')} onClose={() => handleCancel()}>
      <Modal.Header title={t('title')} />
      <Modal.Body>
        <form
          onSubmit={handleSubmit((data) => {
            trackClicked();
            mutation.mutate(data.organizationName);
          })}
          id={FORM_ID}
        >
          <div>
            <S.Label htmlFor={INPUT_LABEL}>{t('input-label')}</S.Label>
            <Input
              id={INPUT_LABEL}
              name="organizationName"
              inputRef={register({
                required: t('required-error') as string,
              })}
              isError={!!errors.organizationName}
              placeholder={t('input-placeholder')}
              errorMessage={errors.organizationName?.message}
            />
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button kind="ghost" onClick={() => handleCancel()} isDisabled={mutation.isLoading}>
          {t('cancel')}
        </Button>
        <Button kind="primary" type="submit" form={FORM_ID} isLoading={mutation.isLoading}>
          {t('update')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
